/**
 * CampusAccessControl - Security Guard Dashboard for managing campus entry/exit
 * Features:
 * - Delegate QR scanning for quick campus entry
 * - Full visitor registration with vehicle info
 * - Photo capture for visitors and vehicles
 * - Real-time campus log
 * - Check-out processing
 */
import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  QrCode, UserPlus, Car, LogOut, Search, Users, Clock,
  Camera, Upload, CheckCircle, XCircle, AlertCircle, RefreshCw,
  Truck, Bike, MapPin, Phone, Mail, Building, FileText,
  Shield, Eye, ChevronRight, Loader2, ScanLine
} from 'lucide-react';
import UniversalScanner from './UniversalScanner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Purpose options for visitors
const PURPOSE_OPTIONS = [
  { value: 'delivery', label: 'Delivery', icon: Truck },
  { value: 'meeting', label: 'Meeting/Appointment', icon: Users },
  { value: 'contractor', label: 'Contractor/Service', icon: Building },
  { value: 'event', label: 'Event Guest', icon: Users },
  { value: 'vip', label: 'VIP Guest', icon: Shield },
  { value: 'other', label: 'Other', icon: FileText },
];

// Vehicle types
const VEHICLE_TYPES = [
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'bicycle', label: 'Bicycle' },
  { value: 'other', label: 'Other' },
];

// ID types
const ID_TYPES = [
  { value: 'national_id', label: 'National ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'work_id', label: 'Work ID' },
  { value: 'other', label: 'Other' },
];

const CampusAccessControl = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('entry');
  const [scanMode, setScanMode] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const qrInputRef = useRef(null);

  // Auth token (zustand store first, legacy key fallback)
  const getToken = () => {
    try {
      const s = localStorage.getItem('eventflow-auth');
      if (s) { const p = JSON.parse(s); if (p?.state?.token) return p.state.token; }
    } catch { /* noop */ }
    return localStorage.getItem('eventflow_token') || '';
  };

  // Fetch campus stats
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['campus-stats', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/campus-stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      return res.json();
    },
    refetchInterval: 10000, // Refresh every 10s
  });

  // Fetch campus log
  const { data: campusLog = [], refetch: refetchLog } = useQuery({
    queryKey: ['campus-log', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/campus-log?limit=50`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 5000,
  });

  // Fetch people currently on campus
  const { data: onCampus = [], refetch: refetchOnCampus } = useQuery({
    queryKey: ['campus-log-oncampus', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/campus-log?status=on_campus&limit=100`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 5000,
  });

  // Scan delegate QR
  const scanDelegateMutation = useMutation({
    mutationFn: async (qrCode) => {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/campus-entry/delegate/scan?qr_code=${encodeURIComponent(qrCode)}&gate=main`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Scan failed');
      return res.json();
    },
    onSuccess: (data) => {
      if (data.status === 'already_on_campus') {
        toast.info(`${data.delegate?.name} is already on campus`);
      } else {
        toast.success(`Welcome, ${data.delegate?.name}!`);
      }
      refetchStats();
      refetchLog();
      setQrInput('');
    },
    onError: (err) => {
      toast.error(err.message || 'Scan failed');
    }
  });

  // Check out
  const checkoutMutation = useMutation({
    mutationFn: async (entryId) => {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/campus-exit/${entryId}?gate=main`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Checkout failed');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Checked out successfully');
      refetchStats();
      refetchLog();
      refetchOnCampus();
      queryClient.invalidateQueries(['campus-log-oncampus']);
    },
    onError: () => toast.error('Checkout failed')
  });

  // Handle QR scan input
  const handleQrScan = useCallback((e) => {
    if (e.key === 'Enter' && qrInput.trim()) {
      scanDelegateMutation.mutate(qrInput.trim());
    }
  }, [qrInput, scanDelegateMutation]);

  // Focus QR input when scan mode is active
  React.useEffect(() => {
    if (scanMode && qrInputRef.current) {
      qrInputRef.current.focus();
    }
  }, [scanMode]);

  return (
    <div className="space-y-6" data-testid="campus-access-control">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Campus Access Control
          </h1>
          <p className="text-sm text-muted-foreground">Security checkpoint management</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { refetchStats(); refetchLog(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">On Campus</p>
                <p className="text-3xl font-bold text-blue-700">{stats?.currently_on_campus?.total || 0}</p>
              </div>
              <Users className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Delegates</p>
                <p className="text-3xl font-bold text-emerald-700">{stats?.currently_on_campus?.delegates || 0}</p>
              </div>
              <QrCode className="h-10 w-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Visitors</p>
                <p className="text-3xl font-bold text-amber-700">{stats?.currently_on_campus?.visitors || 0}</p>
              </div>
              <UserPlus className="h-10 w-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Vehicles</p>
                <p className="text-3xl font-bold text-purple-700">{stats?.currently_on_campus?.vehicles || 0}</p>
              </div>
              <Car className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="entry" className="gap-2">
            <QrCode className="h-4 w-4" />
            Entry
          </TabsTrigger>
          <TabsTrigger value="exit" className="gap-2">
            <LogOut className="h-4 w-4" />
            Exit
          </TabsTrigger>
          <TabsTrigger value="oncampus" className="gap-2">
            <Users className="h-4 w-4" />
            On Campus ({onCampus.length})
          </TabsTrigger>
          <TabsTrigger value="log" className="gap-2">
            <FileText className="h-4 w-4" />
            Log
          </TabsTrigger>
        </TabsList>

        {/* Entry Tab */}
        <TabsContent value="entry" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Delegate QR Scan - Using UniversalScanner with camera support */}
            <UniversalScanner
              eventId={eventId}
              mode="gate_entry"
              onScanSuccess={(data) => {
                refetchStats();
                refetchOnCampus();
              }}
            />

            {/* Visitor Registration */}
            <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <UserPlus className="h-5 w-5" />
                  Visitor Entry
                </CardTitle>
                <CardDescription>Register walk-in visitors, deliveries, contractors</CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={showVisitorForm} onOpenChange={setShowVisitorForm}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-amber-600 hover:bg-amber-700" size="lg">
                      <UserPlus className="h-5 w-5 mr-2" />
                      Register New Visitor
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Register Visitor</DialogTitle>
                    </DialogHeader>
                    <VisitorRegistrationForm
                      eventId={eventId}
                      onSuccess={() => {
                        setShowVisitorForm(false);
                        refetchStats();
                        refetchLog();
                      }}
                    />
                  </DialogContent>
                </Dialog>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PURPOSE_OPTIONS.slice(0, 3).map((purpose) => (
                    <Button
                      key={purpose.value}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setShowVisitorForm(true)}
                    >
                      <purpose.icon className="h-3 w-3 mr-1" />
                      {purpose.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Entries */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Recent Entries (Today)</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                {campusLog.filter(e => e.status === 'on_campus').length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No entries today</p>
                ) : (
                  <div className="space-y-2">
                    {campusLog.filter(e => e.status === 'on_campus').slice(0, 10).map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            {entry.entry_type === 'delegate' ? (
                              <QrCode className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <UserPlus className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{entry.person_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {entry.entry_type} • {entry.gate} • {new Date(entry.entry_time).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        {entry.has_vehicle && (
                          <Badge variant="outline" className="gap-1">
                            <Car className="h-3 w-3" />
                            {entry.vehicle_plate}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exit Tab */}
        <TabsContent value="exit" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Exit Scanner - Using UniversalScanner for exit processing */}
            <UniversalScanner
              eventId={eventId}
              mode="gate_exit"
              onScanSuccess={(data) => {
                refetchStats();
                refetchLog();
                queryClient.invalidateQueries(['campus-log-oncampus']);
              }}
            />

            {/* Manual Exit Processing */}
            <Card className="border-2 border-dashed border-red-300 bg-red-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <LogOut className="h-5 w-5" />
                  Manual Exit
                </CardTitle>
                <CardDescription>Process exit for people without QR codes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search by name or vehicle plate..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {searchQuery && (
                    <ScrollArea className="h-[200px] border rounded-lg p-2">
                      {onCampus
                        .filter(e => 
                          e.person_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          e.vehicle_plate?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .slice(0, 5)
                        .map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <div className={`p-1 rounded-full ${entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                                {entry.entry_type === 'delegate' ? (
                                  <QrCode className="h-3 w-3 text-emerald-600" />
                                ) : (
                                  <UserPlus className="h-3 w-3 text-amber-600" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{entry.person_name}</p>
                                {entry.has_vehicle && (
                                  <p className="text-xs text-gray-500">{entry.vehicle_plate}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => checkoutMutation.mutate(entry.id)}
                              disabled={checkoutMutation.isPending}
                            >
                              <LogOut className="h-3 w-3 mr-1" />
                              Exit
                            </Button>
                          </div>
                        ))}
                      {searchQuery && onCampus.filter(e => 
                        e.person_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        e.vehicle_plate?.toLowerCase().includes(searchQuery.toLowerCase())
                      ).length === 0 && (
                        <p className="text-center text-gray-500 py-4">No matches found</p>
                      )}
                    </ScrollArea>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Exits */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Recent Exits (Today)</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                {campusLog.filter(e => e.status === 'exited').length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No exits today</p>
                ) : (
                  <div className="space-y-2">
                    {campusLog.filter(e => e.status === 'exited').slice(0, 10).map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            {entry.entry_type === 'delegate' ? (
                              <QrCode className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <UserPlus className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{entry.person_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {entry.entry_type} • {entry.gate} • 
                              Exit: {entry.exit_time ? new Date(entry.exit_time).toLocaleTimeString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        {entry.has_vehicle && (
                          <Badge variant="outline" className="gap-1">
                            <Car className="h-3 w-3" />
                            {entry.vehicle_plate}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exit Tab */}
        <TabsContent value="exit" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Exit QR Scanner */}
            <UniversalScanner
              eventId={eventId}
              mode="gate_exit"
              onScanSuccess={(data) => {
                refetchStats();
                refetchOnCampus();
              }}
            />

            {/* Manual Exit by Search */}
            <Card className="border-2 border-dashed border-purple-300 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Search className="h-5 w-5" />
                  Search & Exit
                </CardTitle>
                <CardDescription>Find person to check out manually</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Search by name..."
                  className="bg-white"
                />
                <ScrollArea className="h-48">
                  {onCampus.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No one on campus</p>
                  ) : (
                    <div className="space-y-2">
                      {onCampus.slice(0, 5).map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-full ${entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                              {entry.entry_type === 'delegate' ? (
                                <QrCode className="h-3 w-3 text-emerald-600" />
                              ) : (
                                <UserPlus className="h-3 w-3 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{entry.person_name}</p>
                              <p className="text-xs text-muted-foreground">
                                In: {new Date(entry.entry_time).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-purple-600 border-purple-300 hover:bg-purple-100"
                            onClick={() => checkoutMutation.mutate(entry.id)}
                            disabled={checkoutMutation.isPending}
                          >
                            <LogOut className="h-3 w-3 mr-1" />
                            Exit
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* On Campus Tab */}
        <TabsContent value="oncampus" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>Currently On Campus</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search by name, plate..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {onCampus.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No one currently on campus</p>
                ) : (
                  <div className="space-y-2">
                    {onCampus
                      .filter(e => !searchQuery || 
                        e.person_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        e.vehicle_plate?.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                              {entry.entry_type === 'delegate' ? (
                                <QrCode className="h-5 w-5 text-emerald-600" />
                              ) : (
                                <UserPlus className="h-5 w-5 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold">{entry.person_name}</p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Badge variant="secondary" className="text-xs">
                                  {entry.entry_type}
                                </Badge>
                                <span>•</span>
                                <Clock className="h-3 w-3" />
                                <span>In: {new Date(entry.entry_time).toLocaleTimeString()}</span>
                                {entry.purpose && (
                                  <>
                                    <span>•</span>
                                    <span>{entry.purpose}</span>
                                  </>
                                )}
                              </div>
                              {entry.has_vehicle && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Car className="h-3 w-3 text-purple-500" />
                                  <span className="text-sm font-mono">{entry.vehicle_plate}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => checkoutMutation.mutate(entry.id)}
                            disabled={checkoutMutation.isPending}
                          >
                            <LogOut className="h-4 w-4 mr-1" />
                            Check Out
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Log Tab */}
        <TabsContent value="log" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Campus Access Log</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {campusLog.map((entry) => (
                    <div
                      key={entry.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        entry.status === 'on_campus' ? 'bg-green-50 border-green-200' : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          entry.entry_type === 'delegate' ? 'bg-emerald-100' : 'bg-amber-100'
                        }`}>
                          {entry.entry_type === 'delegate' ? (
                            <QrCode className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <UserPlus className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{entry.person_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {entry.entry_type} • {entry.gate}
                            {entry.has_vehicle && ` • ${entry.vehicle_plate}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-600">
                            IN: {new Date(entry.entry_time).toLocaleTimeString()}
                          </span>
                          {entry.exit_time ? (
                            <span className="text-xs text-red-600">
                              OUT: {new Date(entry.exit_time).toLocaleTimeString()}
                            </span>
                          ) : (
                            <Badge variant="default" className="bg-green-600 text-xs">
                              ON CAMPUS
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Visitor Registration Form Component
const VisitorRegistrationForm = ({ eventId, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    id_number: '',
    id_type: 'national_id',
    phone: '',
    email: '',
    company: '',
    purpose: 'delivery',
    purpose_details: '',
    visiting_person: '',
    has_vehicle: false,
    vehicle_plate: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_type: 'car',
    passengers_count: 1,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getToken = () => {
    try {
      const s = localStorage.getItem('eventflow-auth');
      if (s) { const p = JSON.parse(s); if (p?.state?.token) return p.state.token; }
    } catch { /* noop */ }
    return localStorage.getItem('eventflow_token') || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name) {
      toast.error('Full name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/visitors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error('Registration failed');

      const visitor = await res.json();
      toast.success(`Visitor registered! Pass: ${visitor.pass_code}`);
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Info */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Personal Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Full Name *</Label>
            <Input
              value={formData.full_name}
              onChange={(e) => updateField('full_name', e.target.value)}
              placeholder="Enter full name"
              required
            />
          </div>
          <div>
            <Label>ID Type</Label>
            <Select value={formData.id_type} onValueChange={(v) => updateField('id_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ID_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>ID Number</Label>
            <Input
              value={formData.id_number}
              onChange={(e) => updateField('id_number', e.target.value)}
              placeholder="ID/Passport number"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder="+254..."
            />
          </div>
          <div>
            <Label>Company/Organization</Label>
            <Input
              value={formData.company}
              onChange={(e) => updateField('company', e.target.value)}
              placeholder="Company name"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Visit Details */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Visit Details
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Purpose of Visit *</Label>
            <Select value={formData.purpose} onValueChange={(v) => updateField('purpose', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visiting Person/Department</Label>
            <Input
              value={formData.visiting_person}
              onChange={(e) => updateField('visiting_person', e.target.value)}
              placeholder="Who are they visiting?"
            />
          </div>
          <div className="col-span-2">
            <Label>Additional Details</Label>
            <Textarea
              value={formData.purpose_details}
              onChange={(e) => updateField('purpose_details', e.target.value)}
              placeholder="Brief description of visit purpose..."
              rows={2}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Vehicle Info */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Car className="h-4 w-4" />
            Vehicle Information
          </h3>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.has_vehicle}
              onCheckedChange={(v) => updateField('has_vehicle', v)}
              id="has-vehicle"
            />
            <Label htmlFor="has-vehicle">Has Vehicle</Label>
          </div>
        </div>

        {formData.has_vehicle && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-purple-50 rounded-lg">
            <div>
              <Label>License Plate *</Label>
              <Input
                value={formData.vehicle_plate}
                onChange={(e) => updateField('vehicle_plate', e.target.value.toUpperCase())}
                placeholder="KAA 123A"
                className="uppercase"
              />
            </div>
            <div>
              <Label>Vehicle Type</Label>
              <Select value={formData.vehicle_type} onValueChange={(v) => updateField('vehicle_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Make</Label>
              <Input
                value={formData.vehicle_make}
                onChange={(e) => updateField('vehicle_make', e.target.value)}
                placeholder="Toyota, Honda..."
              />
            </div>
            <div>
              <Label>Model</Label>
              <Input
                value={formData.vehicle_model}
                onChange={(e) => updateField('vehicle_model', e.target.value)}
                placeholder="Corolla, Civic..."
              />
            </div>
            <div>
              <Label>Color</Label>
              <Input
                value={formData.vehicle_color}
                onChange={(e) => updateField('vehicle_color', e.target.value)}
                placeholder="White, Black..."
              />
            </div>
            <div>
              <Label>Passengers</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={formData.passengers_count}
                onChange={(e) => updateField('passengers_count', parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4 mr-2" />
          )}
          Register & Issue Pass
        </Button>
      </div>
    </form>
  );
};

export default CampusAccessControl;
